import { Card, FlexEnd, Headline4 } from 'components/Elements';
import FileUpload from 'components/fileUpload/FileUpload';
import { FileMetaData, FileType } from 'components/fileUpload/strategies/strategies';
import { NotificationType } from 'components/notifiers/Notification';
import { MainSectionWithGaps } from 'components/sections/MainSection';
import useModifyRequest from 'hooks/useModifyRequest';
import useUploadRequest from 'hooks/useUploadRequest';
import { DOCUMENTS_URL } from 'lib/_api-helpers';
import { FileDocument, FileDocumentType } from 'models/FileDocument';
import { DocumentRequest } from 'models/inputModels/DocumentRequest';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import strings from '../../../../../strings/strings.json';
import Notification from 'components/notifiers/Notification';
import UploadIcon from 'assets/icons/controls/UploadIcon';
import { IconSize } from 'assets/icons/icon-sizes';
import { theme } from 'theme';
import Hint from 'components/Hint';
import Button from 'components/buttons/Button';

type UploadVideoProps = {
    activityId: string;
    liveTrainingId: string;
    scheduledCourseId: string;
    s3FilePath: string | undefined;
    refetchData: Function;
};

export default function UploadVideo(uploadVideoProps: UploadVideoProps) {
    const navigate = useNavigate();

    const [tempFileFromLocal, setTempFileFromLocal] = React.useState<File | undefined>();
    const [fileValid, setFileValid] = React.useState<boolean>(false);
    const [fileMetaDataValid, setFileMetaDataValid] = React.useState<boolean>(false);
    const [fileMetaData, setFileMetaData] = React.useState<FileMetaData | undefined>(undefined);
    const [error, setError] = React.useState<string | null>(null);

    const { upload, uploading } = useUploadRequest();

    const { modifyData: createDocument, loading: createLoading } = useModifyRequest<DocumentRequest, FileDocument>(
        'documents',
        'POST',
    );
    const { modifyData: attachVideoToActivity, loading: attachVideoLoading } = useModifyRequest(
        `activities/live-training/${uploadVideoProps.activityId}/video`,
        'PATCH',
    );

    async function uploadFile() {
        setError(null);

        if (!tempFileFromLocal) {
            setError(strings.fileUploadPage.noFileText);
            return;
        }
        if (!fileValid || !fileMetaDataValid) return;

        const uploadResponse = await upload(tempFileFromLocal);
        if (uploadResponse.errors && uploadResponse.errors.length > 0) {
            setError(uploadResponse.errors[0].code);
            return;
        }
        if (!uploadResponse.value) {
            setError('Error getting presigned URL.');
            return;
        }

        const response = await createDocument({
            fileName: uploadResponse.value.filename,
            filePath: uploadResponse.value.s3Key,
            type: FileDocumentType.VIDEO,
            videoTranscript: fileMetaData?.videoTranscript,
            imageAltText: fileMetaData?.imageAltText,
        });

        const { errors } = response;
        if (errors) {
            setError(errors[0].code);
            return;
        }
        if (!response.value) {
            setError('Error saving document.');
            return;
        }

        await attachVideoToActivity({
            videoId: response.value.id,
            liveTrainingId: uploadVideoProps.liveTrainingId,
        });

        await uploadVideoProps.refetchData();

        navigate(
            `/live-course/${uploadVideoProps.scheduledCourseId}/live-training/${uploadVideoProps.liveTrainingId}`,
            {
                state: { activeTabId: 'upload-video-tab' },
            },
        );
    }

    return (
        <MainSectionWithGaps>
            {uploadVideoProps.s3FilePath && (
                <>
                    <Headline4>{strings.liveTrainingDetailPage.uploadVideoTab.heading}</Headline4>
                    <Hint hint={strings.liveTrainingDetailPage.uploadVideoTab.hint} />
                    <video
                        controls
                        controlsList="nodownload"
                        src={`${DOCUMENTS_URL}/${uploadVideoProps.s3FilePath}`}
                        width="100%"
                    />
                </>
            )}
            <Card>
                <FileUpload
                    localFile={tempFileFromLocal}
                    setLocalFile={(newFile: File | undefined) => {
                        setTempFileFromLocal(newFile);
                    }}
                    fileMetaData={fileMetaData}
                    setFileMetaData={(newFileMetaData: FileMetaData | undefined) => {
                        setFileMetaData(newFileMetaData);
                    }}
                    fileValid={fileValid}
                    setFileValid={(fileValid: boolean) => {
                        setFileValid(fileValid);
                    }}
                    fileMetaDataValid={fileMetaDataValid}
                    setFileMetaDataValid={(fileMetaDataValid: boolean) => {
                        setFileMetaDataValid(fileMetaDataValid);
                    }}
                    chosenFileType={FileType.VIDEO}
                />
            </Card>
            {error && (
                <Notification
                    notificationType={NotificationType.DANGER}
                    title={strings.fileUploadPage.errorTitle}
                    description={error ?? ''}
                    maxWidth={'100%'}
                />
            )}
            <FlexEnd>
                <Button
                    title={strings.liveTrainingDetailPage.uploadVideoTab.uploadButtonTitle}
                    aria={strings.liveTrainingDetailPage.uploadVideoTab.uploadButtonAria}
                    icon={<UploadIcon colour={theme.cardBackground} size={IconSize.SMALL} />}
                    onClick={uploadFile}
                    submitting={uploading || createLoading || attachVideoLoading}
                />
            </FlexEnd>
        </MainSectionWithGaps>
    );
}
